import React from "react";
import { usePutSegmentCleanupHistoryQuery } from "@tiny/api";

export interface CleanedListHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  segmentId: string;
}

export function useCleanedListHistoryDialog(
  props: CleanedListHistoryDialogProps
) {
  const { segmentId, open } = props;

  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const itemsPerPageOptions = [10, 25, 50];

  const {
    data: { histories: content = [] } = {},
    isLoading,
    refetch,
  } = usePutSegmentCleanupHistoryQuery(
    { segmentId },
    {
      enabled: !!segmentId && open,
    }
  );

  const handlePageChange = React.useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSizeChange = React.useCallback((newSize: number) => {
    setItemsPerPage(newSize);
    setPage(0);
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearch(value);
    setPage(0);
  }, []);

  // Filter and paginate
  const filteredContent = React.useMemo(() => {
    if (!search) return content;
    return content.filter((item) =>
      item.fileName?.toLowerCase().includes(search.toLowerCase())
    );
  }, [content, search]);

  const paginatedContent = React.useMemo(() => {
    const start = page * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredContent.slice(start, end);
  }, [filteredContent, page, itemsPerPage]);

  const filteredTotalCount = filteredContent.length;

  return {
    content: paginatedContent,
    handlePageChange,
    handleSearchChange,
    handleSizeChange,
    itemsPerPage,
    itemsPerPageOptions,
    page,
    search,
    isLoading,
    totalCount: filteredTotalCount,
    reloadHistories: refetch,
  };
}
